// Email auth uses message/errors; phone OTP also returns stable error codes.
// Keep both contracts intact, and translate the backend's known public messages.
const aliases = {
  "This email is already registered": "email_already_registered",
  "This phone is already registered": "phone_already_registered",
  "Invalid email": "email_invalid",
  "Invalid email address": "email_invalid",
  "Minimum is 8 characters": "password_too_short",
  "This email doesn't exist": "reset_account_not_found",
  "This phone user doesn't exist": "reset_account_not_found",
  "Invalid phone": "contact_phone_invalid",
  "Invalid phone number": "contact_phone_invalid",
  "Missing credentials": "missing_credentials",
  "Missing parameters": "missing_verification_details",
  "Invalid login credentials": "invalid_credentials",
  "Invalid verification code": "verification_code_incorrect",
  "This login method is currently unavailable": "method_unavailable",
  "Method Disabled": "method_unavailable",
  phone_otp_not_enabled: "method_unavailable",
};

const messages = {
  password_reset_failed: ["We couldn't reset your password. Please try again.", "تعذر استعادة كلمة المرور. حاول مرة أخرى."],
  reset_account_not_found: ["We couldn't find an account with those details. Check them or create an account.", "لم نعثر على حساب بهذه البيانات. تحقق منها أو أنشئ حساباً."],
  password_confirmation_mismatch: ["Your passwords don't match. Enter the same password in both fields.", "كلمتا المرور غير متطابقتين. أدخل نفس كلمة المرور في الحقلين."],
  email_already_registered: ["This email is already registered. Sign in instead.", "هذا البريد الإلكتروني مسجّل بالفعل. اختر تسجيل الدخول."],
  phone_already_registered: ["This phone number is already registered. Sign in instead or contact the store.", "رقم الهاتف مسجّل بالفعل. سجّل الدخول أو تواصل مع المتجر."],
  email_invalid: ["Enter a valid email address.", "أدخل بريداً إلكترونياً صالحاً."],
  password_too_short: ["Use at least 8 characters for your password.", "استخدم 8 أحرف على الأقل لكلمة المرور."],
  contact_phone_invalid: ["Enter a valid phone number with its country code.", "أدخل رقم هاتف صالحاً مع مفتاح الدولة."],
  missing_credentials: ["Enter your email and password.", "أدخل بريدك الإلكتروني وكلمة المرور."],
  missing_verification_details: ["Enter your sign-in details and verification code.", "أدخل بيانات تسجيل الدخول ورمز التحقق."],
  invalid_code: ["Enter the six-digit code we sent you.", "أدخل رمز التحقق المكوّن من ستة أرقام."],
  verification_code_incorrect: ["That code doesn't match. Check the code or request a new one.", "الرمز غير صحيح. تحقق منه أو اطلب رمزاً جديداً."],
  phone_otp_expired: ["This code has expired. Request a new code below.", "انتهت صلاحية الرمز. اطلب رمزاً جديداً أدناه."],
  phone_otp_verification_locked: ["Too many incorrect attempts. Request a new code below.", "تجاوزت عدد المحاولات. اطلب رمزاً جديداً أدناه."],
  phone_otp_cooldown: ["Please wait for the countdown before requesting another code.", "انتظر انتهاء العد التنازلي لطلب رمز جديد."],
  phone_otp_rate_limited: ["Too many codes requested. Try later or use another sign-in method.", "تم طلب رموز كثيرة. حاول لاحقاً أو اختر طريقة أخرى."],
  invalid_credentials: ["Check your sign-in details, or choose Create an account if you're new here.", "تحقق من بياناتك أو اختر إنشاء حساب إذا كنت عميلاً جديداً."],
  method_unavailable: ["This sign-in method is unavailable. Choose another method or try later.", "هذه الطريقة غير متاحة. اختر طريقة أخرى أو حاول لاحقاً."],
};

const record = (value) => value && typeof value === "object" && !Array.isArray(value);
const knownMessage = (value, arabic) => typeof value === "string"
  ? messages[aliases[value] || value]?.[arabic ? 1 : 0] || "" : "";
const publicMessage = (value, arabic) => {
  if (typeof value !== "string") return "";
  const text = value.trim();
  // Never show HTML error pages, objects, or an unrecognized machine code.
  if (!text || text.length > 500 || /<[^>]*>|^[a-z\d]+(?:_[a-z\d]+)+$/i.test(text)) return "";
  return knownMessage(text, arabic) || text;
};

export const getCheckoutAuthError = (error, { language = "english", method, signup = false, verification = false } = {}) => {
  const fields = {};
  if (!error) return { message: "", fields };
  const arabic = language === "arabic";
  const response = record(error) ? error : { code: error };
  const visibleFields = method ? [method === "phone_otp" ? "phone" : "email", verification ? "code" : "password"] : [];
  const generalErrors = [];
  if (record(response.errors)) {
    Object.entries(response.errors).forEach(([field, value]) => {
      const text = [...new Set((Array.isArray(value) ? value : [value])
        .map((item) => publicMessage(item, arabic)).filter(Boolean))].join(" ");
      if (!text) return;
      if (visibleFields.includes(field)) fields[field] = text;
      else generalErrors.push(text);
    });
  }
  const message = knownMessage(response.code, arabic) || publicMessage(response.message, arabic) || generalErrors.join(" ");
  if (message || Object.keys(fields).length) return { message, fields };
  return {
    fields,
    message: verification
      ? arabic ? "تعذر التحقق من الرمز. حاول مرة أخرى." : "We couldn't verify your code. Please try again."
      : signup
        ? arabic ? "تعذر إنشاء حسابك. حاول مرة أخرى." : "We couldn't create your account. Please try again."
        : arabic ? "تعذر تسجيل الدخول. حاول مرة أخرى أو اختر طريقة أخرى." : "We couldn't sign you in. Try again or choose another method.",
  };
};
